import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/common/SEO';
import Breadcrumb from '../components/common/Breadcrumb';
import TypeBadge from '../components/pokemon/TypeBadge';
import PokemonGrid from '../components/pokemon/PokemonGrid';
import { getPokemonByType, pokemonTypes } from '../data/pokemonData';

function TypePage() {
  const { type } = useParams();
  const [pokemon, setPokemon] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('id');

  useEffect(() => {
    const fetchPokemon = () => {
      setLoading(true);
      const pokemonOfType = getPokemonByType(type);
      
      let sorted = [...pokemonOfType];
      switch (sortBy) {
        case 'name':
          sorted.sort((a, b) => a.displayName.localeCompare(b.displayName));
          break;
        case 'id':
          sorted.sort((a, b) => a.id - b.id);
          break;
        case 'hp':
          sorted.sort((a, b) => b.baseStats.hp - a.baseStats.hp);
          break;
        case 'attack':
          sorted.sort((a, b) => b.baseStats.attack - a.baseStats.attack);
          break;
        case 'defense':
          sorted.sort((a, b) => b.baseStats.defense - a.baseStats.defense);
          break;
        case 'speed':
          sorted.sort((a, b) => b.baseStats.speed - a.baseStats.speed);
          break;
        default:
          break;
      }
      
      setPokemon(sorted);
      setLoading(false);
    };

    fetchPokemon();
  }, [type, sortBy]);

  const typeInfo = pokemonTypes.find(t => t.name === type);
  
  if (!typeInfo && !loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Type Not Found</h1>
          <p className="text-gray-600">The Pokemon type you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { name: 'Types', path: '/types' },
    { name: typeInfo ? `${typeInfo.name.charAt(0).toUpperCase() + typeInfo.name.slice(1)} Type` : 'Loading...' }
  ];

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": `${typeInfo?.name.charAt(0).toUpperCase() + typeInfo?.name.slice(1)} Type Pokemon`,
    "description": `Complete list of ${typeInfo?.name} type Pokemon with stats, abilities, and detailed information. ${pokemon.length} Pokemon found.`,
    "url": `https://example.com/type/${type}`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "Pokemon Stats Database",
      "url": "https://example.com"
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-50 py-8"
    >
      <SEO
        title={`${typeInfo?.name.charAt(0).toUpperCase() + typeInfo?.name.slice(1)} Type Pokemon - Complete List`}
        description={`Discover all ${typeInfo?.name} type Pokemon with detailed stats, abilities, and information. Complete ${typeInfo?.name} type Pokemon database with ${pokemon.length} Pokemon.`}
        keywords={`${typeInfo?.name} type pokemon, ${typeInfo?.name} pokemon list, pokemon types, ${typeInfo?.name} type stats`}
        url={`/type/${type}`}
        schema={pageSchema}
      />

      <div className="container mx-auto px-4">
        <Breadcrumb items={breadcrumbItems} />

        {/* Header */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center mb-12"
        >
          {typeInfo && (
            <div className="flex justify-center mb-4">
              <TypeBadge type={typeInfo.name} size="lg" clickable={false} />
            </div>
          )}
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {typeInfo?.name.charAt(0).toUpperCase() + typeInfo?.name.slice(1)} Type Pokemon
          </h1>
          <p className="text-xl text-gray-600">
            {loading ? 'Loading...' : `${pokemon.length} Pokemon found`}
          </p>
        </motion.div>

        {/* Type Stats */}
        {typeInfo && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-lg p-6 mb-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Type Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">{pokemon.length}</div>
                <div className="text-gray-600">Total Pokemon</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">
                  {pokemon.filter(p => p.types.length === 1).length}
                </div>
                <div className="text-gray-600">Pure Type</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">
                  {pokemon.filter(p => p.types.length > 1).length}
                </div>
                <div className="text-gray-600">Dual Type</div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Controls */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-md p-6 mb-8"
        >
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <label htmlFor="sort-select" className="text-sm font-medium text-gray-700">
                Sort by:
              </label>
              <select
                id="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="id">Pokedex Number</option>
                <option value="name">Name (A-Z)</option>
                <option value="hp">Highest HP</option>
                <option value="attack">Highest Attack</option>
                <option value="defense">Highest Defense</option>
                <option value="speed">Highest Speed</option>
              </select>
            </div>

            <div className="text-sm text-gray-600">
              {pokemon.length} {typeInfo?.name} type Pokemon
            </div>
          </div>
        </motion.div>

        {/* Pokemon Grid */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <PokemonGrid pokemon={pokemon} loading={loading} />
        </motion.div>

        {/* Empty State */}
        {!loading && pokemon.length === 0 && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-center py-12"
          >
            <div className="text-6xl mb-4">🔍</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">No Pokemon Found</h2>
            <p className="text-gray-600">
              No Pokemon of this type were found in our database.
            </p>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

export default TypePage;